import { css, cx } from '@emotion/css';
import { pxToRem } from '@mos-cat/ds-theme';
import { createComponent, createHook } from 'reakit-system';
import { variant } from 'styled-system';
import { SystemStyleObject } from '@styled-system/css';
import { useBox } from '../Box/Box';
import { useFocusable } from '../hooks/useFocusable';
import { useTheme } from '../hooks/useTheme';
import { useText } from '../Text/Text';
import { WHCM } from '../constants';
import {
  ComposedOptions,
  FieldHTMLProps,
  FieldProps,
  FieldOptions,
} from './types';
import { customStyles } from './inner/customStyles';

export { ComposedOptions, FieldHTMLProps, FieldProps, FieldOptions };

const error = variant({
  prop: 'validation',
  variants: {
    valid: {},
    invalid: {
      borderColor: 'errorColor',
      ':hover': {
        borderColor: 'errorColor',
      },

      [WHCM]: {
        '&, &:hover': {
          borderColor: 'Highlight',
        },
      },
    },
  },
});

// Отдельным хуком, чтобы он переписал значения border
let useValidation = createHook<{ hasError: boolean }, FieldHTMLProps>({
  keys: ['hasError'],
  compose: [useTheme],
  useOptions(options) {
    return { ...options, validation: options.hasError ? 'invalid' : 'valid' };
  },
  useProps(options, props) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- не можем влиять на типы из styled-system
    return { ...props, className: cx(props.className, css(error(options))) };
  },
});

export const useField = createHook<FieldOptions, FieldHTMLProps>({
  name: 'Field',
  keys: ['hasError', 'disabled'],
  compose: [useBox, useText, useFocusable, useValidation],
  useOptions(options: FieldOptions, props: FieldHTMLProps): ComposedOptions {
    let WebkitTextFillColor = props.disabled
      ? 'fieldDisabledTextColor'
      : undefined;
    return {
      ...options,
      border: 1,
      borderColor: 'fieldBorderColor',
      borderRadius: 'fieldBorderRadius',
      maxWidth: 'semi-wide',
      width: '100%',
      paddingX: 'fieldPaddingX',
      paddingY: 'fieldPaddingY',
      backgroundColor: 'fieldBgColor',
      minHeight: pxToRem(48),
      fontSize: 'textBase',
      color: 'fieldTextColor',
      WebkitTextFillColor,

      styles: {
        ...options.styles,
        ...customStyles,
      } as SystemStyleObject,
    };
  },
  useProps({ disabled }, props) {
    let hasValue = props.value ?? props.defaultValue ?? '';

    return {
      ...props,
      'aria-disabled': disabled,
      onChange: disabled ? undefined : props.onChange,
      onKeyDown: disabled
        ? (event) => {
            if (event.key !== 'Tab') {
              event.preventDefault();
            }
          }
        : props.onKeyDown,
      value: disabled ? hasValue : props.value,
      className: cx(css({ label: 'Field' }), props.className),
    };
  },
});

export const Field = createComponent({
  as: 'input',
  useHook: useField,
  memo: true,
});
